<script context="module">
  import { isRTL } from "sbelt/text";
  import Overlay from "./Overlay.svelte";
  import Tooltip from "@ui/Tooltip.svelte";

  const options = { year: "numeric", month: "long", day: "numeric" };

  function formatDate(date) {
    return new Date(date).toLocaleDateString("en-US", options);
  }
</script>

<script>
  export let newsletter;
  export let hideOverlay;
  const canEdit = newsletter.isDeleted ? false : newsletter.isDraft ? (newsletter.permissions.canEdit ? true : false) : true;
  const editUrl = canEdit ? newsletter.urls.url : undefined;
</script>

<div
  class:border-dashed={newsletter.isDraft}
  class:border-gray-100={newsletter.isDraft}
  class:border-transparent={!newsletter.isDraft}
  class:clickable={canEdit}
  class="newsletter-container relative rounded-md border-4">
  <a
    class="newsletter-wrapper relative block overflow-hidden rounded-md border bg-gray-100 text-brand-gray-70 shadow hover:border-blue-500"
    class:cursor-default={!canEdit}
    href={editUrl}
    alt={newsletter.title}>
    <div class="absolute inset-0">
      <img
        class:grayscale={newsletter.isDraft}
        src={newsletter.urls.thumbnail}
        alt={newsletter.name}
        class="screenshot absolute w-full object-cover" />
    </div>
    <div class="absolute bottom-0 left-0 right-0 flex flex-col items-start bg-white p-3 text-center">
      <div
        dir={isRTL(newsletter.title) ? "rtl" : "ltr"}
        class="newsletter-title relative truncate text-base font-medium"
        class:text-nxgray-600={!newsletter.isDraft}
        class:text-gray-500={newsletter.isDraft}>
        {newsletter.title}
      </div>
      <div class="mb-3 flex items-center justify-start text-xs">
        {#if newsletter.isPublic}
          <div>Public&nbsp;&nbsp;|&nbsp;&nbsp;</div>
        {/if}
        {#if newsletter.isDraft}
          <div class="text-gray-500">Draft</div>
        {:else}
          <div>Last edited {formatDate(newsletter.timestamps.lastUpdated)}</div>
        {/if}
      </div>

      <div class="flex w-full items-center">
        <div class="flex w-full items-center justify-between">
          <div class="flex items-center justify-start">
            {#if !newsletter.isDraft}
              <div class="flex items-center justify-start">
                <span class="material-icons text-18 text-nxgray-200">visibility</span>
                <div class="ml-1 text-sm">{newsletter.metrics.views}</div>
              </div>
            {/if}
          </div>
          <div class="relative z-20 flex h-6 w-6 items-center justify-center overflow-hidden rounded-full bg-gray-100 text-13">
            {#if newsletter.author.name && newsletter.author.urls.avatar}
              {newsletter.author.initials}
              <div class="absolute inset-0 bg-contain bg-no-repeat" style="background-image: url({newsletter.author.urls.avatar})" />
              <Tooltip>{newsletter.author.name}</Tooltip>
            {/if}
          </div>
        </div>
        {#if newsletter.isDraft}
          <div
            class="absolute left-0 top-0 -mt-8 ml-3 rounded border border-gray-700 bg-gray-600 text-11 font-bold uppercase tracking-wider text-white"
            style="padding: 1px 3px 0 3px">
            Draft
          </div>
        {:else if newsletter.hasChanges && newsletter.permissions && newsletter.permissions.canEdit}
          <div
            class="absolute left-0 top-0 -mt-8 ml-3 rounded border border-red-700 bg-red-500 text-11 font-bold uppercase tracking-wider text-white"
            style="padding: 1px 3px 0 3px">
            Unpublished Changes
          </div>
        {/if}
      </div>
    </div>
  </a>
  {#if !hideOverlay}
    <a
      class="newsletter-overlay pointer-events-none absolute inset-0 opacity-0 transition-all duration-200 ease-in-out"
      class:cursor-default={!canEdit}
      href={editUrl}
      alt={newsletter.title}>
      <div class="absolute inset-0 bg-gray-100 opacity-50" />
      <Overlay bind:newsletter />
    </a>
  {/if}
</div>

<style>
  .newsletter-container {
    width: 100%;
    height: 100%;
  }

  @supports (animation-timeline: view()) {
    .newsletter-container {
      opacity: 0;
      animation: fade-in linear;
      /* Hide the card when it reaches 10px from the toolbar */
      animation-timeline: view(y calc(10px + var(--full-toolbar-height, 60px)) 0px);
    }

    @keyframes fade-in {
      10% {
        opacity: 1;
      }
      90% {
        opacity: 1;
      }
      100% {
        opacity: 0;
      }
    }
  }

  .grayscale {
    filter: grayscale(1);
  }

  .newsletter-wrapper {
    width: 100%;
    height: 100%;
    padding-bottom: 75%;
  }

  @media (min-width: 768px) {
    .newsletter-wrapper {
      width: 100%;
      height: 100%;
      padding-bottom: 100%;
    }
  }

  .newsletter-title {
    max-width: 217px;
  }

  .newsletter-container:hover > .newsletter-overlay {
    pointer-events: all;
    opacity: 1;
  }
  .screenshot {
    transform: translateY(1.45rem) scale(1.12);
  }
  .newsletter-container:hover .screenshot {
    animation: slide 8s ease-in-out;
    transition: transform 0.1s;
  }

  @keyframes slide {
    0% {
      transform: translateY(1.45rem) scale(1.12);
    }
    90% {
      transform: translateY(-59%) scale(1.12);
    }
    100% {
      transform: translateY(1.45rem) scale(1.12);
    }
  }
</style>
